import { type AuthUser } from '@/domain/account/types';
import { type BasicUserDTO } from '@/domain/user/types';

import { isUserAdmin } from './permissions';

export interface GenericProduct {
  idTechnologyManager?: number;
  technologyManager?: BasicUserDTO | null;
  idProductOwner?: number;
  productOwner?: BasicUserDTO | null;
  idProductManager?: number;
  productManager?: BasicUserDTO | null;
  idOwner?: number;
  owner?: BasicUserDTO | null;
  cyberSecurityExpertId?: number | null;
  qaSpecialistsIds?: number[];
  qaSpecialists?: BasicUserDTO[];
  productUnitId: number | null;
}

export const isCyberSecurityExpert = (user: AuthUser | undefined, product: GenericProduct | null) =>
  product?.cyberSecurityExpertId === user?.idUser;

export const isTechnologyManager = (user: AuthUser | undefined, product: GenericProduct | null): boolean => {
  if (user == null || product == null) {
    return false;
  }

  if ('idTechnologyManager' in product) {
    return product.idTechnologyManager === user.idUser;
  }
  if ('technologyManager' in product) {
    return product.technologyManager?.idUser === user.idUser;
  }

  return false;
};

export const isProductOwner = (user: AuthUser | undefined, product: GenericProduct | null): boolean => {
  if (user == null || product == null) {
    return false;
  }

  if ('idProductOwner' in product) {
    return product.idProductOwner === user.idUser;
  }
  if ('productOwner' in product) {
    return product.productOwner?.idUser === user.idUser;
  }

  return false;
};

export const isProductManager = (user: AuthUser | undefined, product: GenericProduct | null): boolean => {
  if (user == null || product == null) {
    return false;
  }
  if ('idProductManager' in product) {
    return product.idProductManager === user.idUser;
  }
  if ('productManager' in product) {
    return product.productManager?.idUser === user.idUser;
  }
  return false;
};

export const isQaSpecialist = (
  user: AuthUser | undefined,
  product: GenericProduct | null,
  idOwnerRoadmap?: number,
): boolean => {
  if (user == null || product == null) {
    return false;
  }
  if (idOwnerRoadmap === user?.idUser) return false;
  if ('qaSpecialists' in product) {
    return product.qaSpecialists?.some((qa) => qa.idUser === user.idUser) ?? false;
  }
  if ('qaSpecialistsIds' in product) {
    return product.qaSpecialistsIds?.includes(user.idUser) ?? false;
  }
  return false;
};

export const canEditProductDetail = (user: AuthUser | undefined, product: GenericProduct | null): boolean => {
  if (user == null) {
    return false;
  }
  return (
    isUserAdmin(user) ||
    isProductOwner(user, product) ||
    isProductManager(user, product) ||
    isTechnologyManager(user, product)
  );
};

export const canEditProductMaturity = (user: AuthUser | undefined, product: GenericProduct | null): boolean => {
  if (user == null) {
    return false;
  }

  return (
    isUserAdmin(user) ||
    isProductOwner(user, product) ||
    isProductManager(user, product) ||
    isQaSpecialist(user, product)
  );
};

export const canEditProductSecurityLevel = (user: AuthUser | undefined, product: GenericProduct | null): boolean =>
  canEditProductDetail(user, product) || isCyberSecurityExpert(user, product);
